import type { MouseEvent, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { parseURL, stringifyParsedURL } from 'ufo';
import { type LongPressReactEvents, useLongPress } from 'use-long-press';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useUserPlatform, { type ClientHints, useUserHints } from '@/hooks/use-user-platform';
import { makePrompt } from '@/stores/prompter';
import { BUILD_ENV } from '@/utils/constants';
import copyTextToClipboard from '@/utils/copy-text';
import { getStandaloneMode } from '@/utils/get-standalone-mode';
import cn from '@/utils/merge-class-names';

interface Props {
  direct?: boolean;
  href: string;
  className?: string;
}

export default function AnchorLink({ direct = false, href, className, children }: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const { dispatch } = useCommDispatcher();
  const { hints } = useUserHints();
  const { supportsTouch, isEmbedded } = useUserPlatform();

  const externalLink = generateExternalLink(href, hints);

  const handleNavigate = () => {
    if (isEmbedded) {
      dispatch({ event: 'open-url', data: { url: externalLink } });
    } else {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMenu = (event: LongPressReactEvents | MouseEvent): void => {
    if (direct) return;

    event.preventDefault();
    event.stopPropagation();

    makePrompt({
      key: `anchor-${href}`,
      type: 'action-sheet',
      title: href,
      resolutionButtons: [
        {
          label: t('conversation.open-link'),
          resolution: 'navigate',
        },
        {
          label: t('common.copy'),
          resolution: 'copy',
        },
      ],
      meta:
        'clientX' in event
          ? {
              position: [event.clientX, event.clientY],
            }
          : undefined,
      promise: async (resolution) => {
        switch (resolution) {
          case 'navigate':
            handleNavigate();
            break;
          case 'copy':
            await copyTextToClipboard(href, false);
            break;
        }
      },
    });
  };

  const bindLongPress = useLongPress((event) => handleMenu(event), {
    threshold: 600,
    filterEvents: () => supportsTouch,
  });

  return (
    <a
      data-direct={direct}
      target='_blank'
      rel='noreferrer,noopener'
      href={externalLink}
      className={cn('text-blue-600', className)}
      {...(direct || !supportsTouch ? {} : bindLongPress())}
      onClick={
        isEmbedded
          ? (e) => {
              e.preventDefault();
              handleNavigate();
            }
          : undefined
      }
      onContextMenu={direct ? undefined : (e) => handleMenu(e)}>
      {children}
    </a>
  );
}

function generateExternalLink(href: string, hints?: ClientHints): string {
  const isStandalone = getStandaloneMode();

  const url = parseURL(href);
  if (url.protocol === undefined) url.protocol = 'https:';
  if (!url.protocol.startsWith('http')) return href;

  const link = stringifyParsedURL(url);
  if (!isStandalone || !hints || hints.osType === 'desktop') return link;

  if (hints.osName === 'iOS') {
    const osVersion = Number(hints.osVersion.split('.')[0]);
    if (!Number.isNaN(osVersion) && osVersion < 17) {
      return link;
    }
    return `x-safari-${link}`;
  }
  if (hints.osName === 'Android') {
    url.protocol = undefined;
    return (
      `intent://${stringifyParsedURL(url)}` +
      '#Intent;scheme=https;' +
      'action=android.intent.action.VIEW;' +
      'category=android.intent.category.BROWSABLE;' +
      `S.browser_fallback_url=${encodeURIComponent(link)};` +
      'end'
    );
  }

  return link;
}
